// src/App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";
import {
  Calendar,
  DollarSign,
  RefreshCw,
  TicketPercent,
  Plane,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import ItineraryView from "./pages/ItineraryView";
import ExpenseTracker from "./pages/ExpenseTracker";
import CurrencyConverter from "./pages/CurrencyConverter";
import CouponPage from "./pages/CouponPage";
import LoginScreen from "./components/LoginScreen";

// ✅ 引入 Firebase 相關功能
import { db } from "./firebase";
import { collection, query, onSnapshot } from "firebase/firestore";

// 航班資訊元件 (保持不變)
function FlightCard() {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm border border-hojicha-light/30 overflow-hidden shrink-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex justify-between items-center bg-starbucks/5 transition-colors hover:bg-starbucks/10"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-starbucks text-white flex items-center justify-center shrink-0">
            <Plane size={16} />
          </div>
          <div className="text-left">
            <p className="text-xs font-bold text-hojicha uppercase tracking-widest">
              Flight Info
            </p>
            <p className="font-bold text-starbucks-dark">
              國泰航空 CX564 / CX565
            </p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp size={20} className="text-hojicha" />
        ) : (
          <ChevronDown size={20} className="text-hojicha" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 border-t border-dashed border-hojicha-light/30 bg-white">
          <div className="flex justify-between items-center mb-4">
            <div className="text-center w-16">
              <span className="text-2xl font-black text-starbucks-dark block">
                TPE
              </span>
              <p className="text-xs text-gray-400">11:15</p>
            </div>
            <div className="flex-1 flex flex-col items-center px-2">
              <p className="text-[10px] text-hojicha font-bold mb-1">2h 40m</p>
              <div className="w-full h-[2px] bg-hojicha-light relative flex items-center justify-center">
                <Plane size={12} className="text-hojicha rotate-90 absolute" />
              </div>
              <p className="text-[10px] text-starbucks mt-1 font-bold">
                1/21 (三)
              </p>
            </div>
            <div className="text-center w-16">
              <span className="text-2xl font-black text-starbucks-dark block">
                KIX
              </span>
              <p className="text-xs text-gray-400">14:55</p>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-center w-16">
              <span className="text-2xl font-black text-starbucks-dark block">
                KIX
              </span>
              <p className="text-xs text-gray-400">16:15</p>
            </div>
            <div className="flex-1 flex flex-col items-center px-2">
              <p className="text-[10px] text-hojicha font-bold mb-1">3h 15m</p>
              <div className="w-full h-[2px] bg-hojicha-light relative flex items-center justify-center">
                <Plane size={12} className="text-hojicha -rotate-90 absolute" />
              </div>
              <p className="text-[10px] text-starbucks mt-1 font-bold">
                1/25 (日)
              </p>
            </div>
            <div className="text-center w-16">
              <span className="text-2xl font-black text-starbucks-dark block">
                TPE
              </span>
              <p className="text-xs text-gray-400">18:30</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NavItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center justify-center w-16 transition-all duration-300 ${active ? "text-starbucks -translate-y-1" : "text-hojicha/60"}`}
    >
      <div
        className={`p-2 rounded-2xl mb-1 transition-all ${active ? "bg-starbucks text-white shadow-lg shadow-starbucks/30" : ""}`}
      >
        {icon}
      </div>
      <span
        className={`text-[10px] font-bold tracking-wide ${active ? "opacity-100" : "opacity-0 scale-0"} transition-all h-3`}
      >
        {label}
      </span>
    </Link>
  );
}

function Layout({ children, currentUser, currentExpense }) {
  const location = useLocation();
  return (
    <div className="h-screen w-full font-sans flex flex-col overflow-hidden bg-base-cream">
      <header className="bg-warm-paper/95 backdrop-blur-md px-4 py-3 shadow-sm flex justify-between items-center gap-3 shrink-0 z-50">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-starbucks text-white flex items-center justify-center font-serif text-xl font-bold shadow-md border-2 border-white shrink-0">
            {currentUser.charAt(0)}
          </div>
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <p className="text-[10px] font-bold text-hojicha uppercase tracking-widest leading-tight truncate">
              Welcome
            </p>
            <h1 className="text-lg font-bold text-starbucks-dark leading-none truncate">
              {currentUser}
            </h1>
          </div>
        </div>
        <div className="ml-2 flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gold/30 shadow-sm shrink-0 whitespace-nowrap">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0"></div>
          <span className="font-mono font-bold text-starbucks-dark text-sm">
            ¥{currentExpense.toLocaleString()}
          </span>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto pb-24 scroll-smooth">
        <div className="max-w-md mx-auto pt-2">
          {location.pathname === "/" && <FlightCard />}
          {children}
        </div>
      </main>
      <nav className="fixed bottom-0 left-0 w-full bg-warm-paper border-t border-hojicha-light/50 flex justify-around py-2 pb-6 z-50 shadow-[0_-4px_10px_rgba(160,133,106,0.1)] shrink-0">
        <NavItem
          to="/"
          icon={<Calendar size={22} />}
          label="行程"
          active={location.pathname === "/"}
        />
        <NavItem
          to="/coupons"
          icon={<TicketPercent size={22} />}
          label="優惠券"
          active={location.pathname === "/coupons"}
        />
        <NavItem
          to="/expenses"
          icon={<DollarSign size={22} />}
          label="記帳"
          active={location.pathname === "/expenses"}
        />
        <NavItem
          to="/converter"
          icon={<RefreshCw size={22} />}
          label="匯率"
          active={location.pathname === "/converter"}
        />
      </nav>
    </div>
  );
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentExpense, setCurrentExpense] = useState(0);

  // ✅ 新增：在 App 層級監聽 Firebase，一登入就計算金額
  useEffect(() => {
    if (!currentUser) return;

    // 監聽所有消費紀錄
    const q = query(collection(db, "expenses"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const expensesData = snapshot.docs.map((doc) => {
        const data = doc.data();
        // 資料相容性處理 (跟 ExpenseTracker 一樣)
        let finalSplit = data.splitWith;
        if (!finalSplit) {
          finalSplit = data.isPublic
            ? ["Emily", "Candy", "Max", "Amy", "Cindy"]
            : [data.paidBy];
        }
        return { ...data, splitWith: finalSplit };
      });

      // 計算我的總支出
      const total = expensesData.reduce((acc, exp) => {
        if (exp.splitWith && exp.splitWith.includes(currentUser)) {
          const costPerPerson = exp.amount / exp.splitWith.length;
          return acc + costPerPerson;
        }
        return acc;
      }, 0);

      setCurrentExpense(Math.round(total));
    });

    return () => unsubscribe();
  }, [currentUser]); // 當 currentUser 改變時 (登入後) 執行

  if (!currentUser) return <LoginScreen onLogin={setCurrentUser} />;

  return (
    <Router>
      {/* 傳遞 currentUser 給 ItineraryView */}
      <Layout currentUser={currentUser} currentExpense={currentExpense}>
        <Routes>
          <Route
            path="/"
            element={<ItineraryView currentUser={currentUser} />}
          />
          <Route path="/coupons" element={<CouponPage />} />
          <Route
            path="/expenses"
            element={<ExpenseTracker currentUser={currentUser} />}
          />
          <Route path="/converter" element={<CurrencyConverter />} />
        </Routes>
      </Layout>
    </Router>
  );
}
